import { Spacing } from '@persona-id/design-tokens';
import Lottie from 'lottie-react';
import styled from 'styled-components';

import IconChevron from 'images/icons/chevron.svg';
import CreatePersonaCtaIcon from 'images/inquiry/create-persona/cta-icon.svg';
import ImageFingerprint from 'images/inquiry/create-persona/icon-fingerprint.svg';
import ImageShield from 'images/inquiry/create-persona/icon-shield.svg';
import ImageSpeed from 'images/inquiry/create-persona/icon-speed.svg';
import PasskeyRegistrationHero from 'images/inquiry/create-persona/passkey-registration-hero.json';
import CreatePersonaSuccessHero from 'images/inquiry/create-persona/success-hero.json';
import ImageCheck from 'images/inquiry/id-class/check.svg';
import ImageIdDocument from 'images/inquiry/id-class/document.svg';
import ImageFlag from 'images/inquiry/id-class/flag.svg';
import ImageGlobe from 'images/inquiry/id-class/globe.svg';
import GovernmentIdNfcScanBackClosed from 'images/inquiry/id-class/government_id_nfc_scan_back_closed.json';
import GovernmentIdNfcScanBackThenFront from 'images/inquiry/id-class/government_id_nfc_scan_back_then_front.json';
import GovernmentIdNfcScanFrontClosed from 'images/inquiry/id-class/government_id_nfc_scan_front_closed.json';
import GovernmentIdNfcScanIdCard from 'images/inquiry/id-class/government_id_nfc_scan_id_card.json';
import GovernmentIdNfcScanPhotoPage from 'images/inquiry/id-class/government_id_nfc_scan_photo_page.json';
import ImageHome from 'images/inquiry/id-class/home.svg';
import ImageIdHorizontal from 'images/inquiry/id-class/id-horizontal.svg';
import ImagePhone from 'images/inquiry/id-class/phone.svg';
import ImageUser from 'images/inquiry/id-class/user.svg';
import StepperCompletedIcon from 'images/inquiry/stepper/completed.svg';
import StepperConnectorIcon from 'images/inquiry/stepper/connector.svg';
import StepperCurrentIcon from 'images/inquiry/stepper/current.svg';
import StepperErroredIcon from 'images/inquiry/stepper/errored.svg';
import StepperIncompleteIcon from 'images/inquiry/stepper/incomplete.svg';
import VerifyWithPersonaAddressIcon from 'images/inquiry/verify-with-persona/address-icon.svg';
import VerifyWithPersonaIdIcon from 'images/inquiry/verify-with-persona/id-icon.svg';
import VerifyWithPersonaIndividualAttributesIcon from 'images/inquiry/verify-with-persona/individual-attributes-icon.svg';
import VerifyWithPersonaNameIcon from 'images/inquiry/verify-with-persona/name-icon.svg';
import PasskeyAuthenticationHero from 'images/inquiry/verify-with-persona/passkey-authentication-hero.json';
import PersonaLogo from 'images/inquiry/verify-with-persona/persona-logo.svg';
import VerifyWithPersonaSelfieIcon from 'images/inquiry/verify-with-persona/selfie-icon.svg';
import ImageDefaultFailed from 'images/pictographs/default-failed.svg';
import ImageDocument from 'images/pictographs/document.svg';
import ImageDocumentFailed from 'images/pictographs/document-failed.svg';
import ImageIdBackFail from 'images/pictographs/id-back-fail.svg';
import ImageFailed from 'images/pictographs/id-failed.svg';
import ImageIdFrontFail from 'images/pictographs/id-front-fail.svg';
import ImageSelfieFail from 'images/pictographs/selfie-fail.svg';
import StartHero from 'images/pictographs/start-hero.svg';
import VerificationHero from 'inquiry/components/VerificationHero';
import { useApply } from 'inquiry/lib/jsonLogic';
import { replaceColors } from 'inquiry/lib/lottieColorify';
import { colorCss, sizeCss, StyleValueSize } from 'lib/inquiryStyles';
import { ImageKey, ImageLocalComponent } from 'lib/interfaces/ui-components';
import AnimatedCheck from 'verify/components/AnimatedCheck';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import ThemedSvg from 'verify/components/ThemedSvg';
import { Inquiry } from 'verify/models/inquiry';

// NOTE: Spacing[6] is visually 0 becasue the animated check has negative margin for some reason.
const AnimatedCheckContainer = styled.div<{
  marginTop: string;
  marginBottom: string;
  justify?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ justify }) => justify ?? 'center'};
  margin-top: calc(${({ marginTop }) => marginTop ?? '0'} + ${Spacing[6]});
  margin-bottom: calc(${({ marginBottom }) => marginBottom ?? '0'} + ${Spacing[6]});
`;

// Note: added for start-aligned FailureReason ThemedSvg
const FailureReasonPictograph = styled.div<{ justify?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ justify }) => justify ?? 'center'};
`;

export const GOV_ID_OPTION_IMAGE_ASSETS = [
  ImageKey.OptionFlagIcon,
  ImageKey.OptionGlobeIcon,
  ImageKey.OptionHomeIcon,
  ImageKey.OptionIdIcon,
  ImageKey.OptionIdDocumentIcon,
  ImageKey.OptionUserIcon,
  ImageKey.OptionPhoneIcon,
];

export const INLINE_ASSETS_TO_IGNORE_FOR_SCREEN_READER = [
  ImageKey.OptionFlagIcon,
  ImageKey.OptionGlobeIcon,
  ImageKey.OptionHomeIcon,
  ImageKey.OptionIdIcon,
  ImageKey.OptionPhoneIcon,
  ImageKey.OptionIdDocumentIcon,
  ImageKey.OptionUserIcon,
  ImageKey.OptionCheckIcon,
  ImageKey.OptionChevronIcon,
  ImageKey.CreatePersonaCtaCardIcon,
  ImageKey.CreatePersonaPasskeyRegistrationSpeedIcon,
  ImageKey.CreatePersonaPasskeyRegistrationShieldIcon,
  ImageKey.CreatePersonaPasskeyRegistrationFingerprintIcon,
  ImageKey.VerifyWithPersonaPasskeyAuthenticationAddressIcon,
  ImageKey.VerifyWithPersonaPasskeyAuthenticationIdIcon,
  ImageKey.VerifyWithPersonaPasskeyAuthenticationIndividualAttributesIcon,
  ImageKey.VerifyWithPersonaPasskeyAuthenticationNameIcon,
  ImageKey.VerifyWithPersonaPasskeyAuthenticationSelfieIcon,
  ImageKey.StepperConnectorIcon,
];

// Could these live on the server in the component definition?
export const IMAGE_LOCAL_DIMENSION_DEFAULTS: Record<
  ImageKey,
  Record<'width' | 'height', StyleValueSize>
> = {
  [ImageKey.AnimatedCheck]: {
    width: { value: 250, unit: 'px' },
    height: { value: 250, unit: 'px' },
  },
  [ImageKey.DocumentStartHero]: {
    width: { value: 100, unit: '%' },
    height: { value: 184, unit: 'px' },
  },
  [ImageKey.FailedIcon]: {
    width: { value: 140, unit: 'px' },
    height: { value: 120, unit: 'px' },
  },
  [ImageKey.StartHero]: {
    width: { value: 240, unit: 'px' },
    height: { value: 177, unit: 'px' },
  },
  [ImageKey.FailureDefaultIcon]: {
    width: { value: 100, unit: 'px' },
    height: { value: 100, unit: 'px' },
  },
  [ImageKey.FailureDocumentIcon]: {
    width: { value: 149, unit: 'px' },
    height: { value: 120, unit: 'px' },
  },
  [ImageKey.FailureSelfieIcon]: {
    width: { value: 103, unit: 'px' },
    height: { value: 111, unit: 'px' },
  },
  [ImageKey.IdBackIcon]: {
    width: { value: 149, unit: 'px' },
    height: { value: 100, unit: 'px' },
  },
  [ImageKey.IdFrontIcon]: {
    width: { value: 149, unit: 'px' },
    height: { value: 100, unit: 'px' },
  },
  [ImageKey.OptionFlagIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionGlobeIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionHomeIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionIdIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionIdDocumentIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionUserIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionPhoneIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionCheckIcon]: {
    width: { value: 32, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
  [ImageKey.OptionChevronIcon]: {
    width: { value: 8, unit: 'px' },
    height: { value: 14, unit: 'px' },
  },
  [ImageKey.CreatePersonaPasskeyRegistrationHero]: {
    width: { value: 181, unit: 'px' },
    height: { value: 181, unit: 'px' },
  },
  [ImageKey.CreatePersonaCtaCardIcon]: {
    width: { value: 41, unit: 'px' },
    height: { value: 44, unit: 'px' },
  },
  [ImageKey.CreatePersonaPasskeyRegistrationSpeedIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.CreatePersonaPasskeyRegistrationShieldIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.CreatePersonaPasskeyRegistrationFingerprintIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.CreatePersonaSuccessHero]: {
    width: { value: 354, unit: 'px' },
    height: { value: 208, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationPersonaLogo]: {
    width: { value: 128, unit: 'px' },
    height: { value: 128, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationHero]: {
    width: { value: 181, unit: 'px' },
    height: { value: 181, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationAddressIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationIdIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationIndividualAttributesIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationNameIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.VerifyWithPersonaPasskeyAuthenticationSelfieIcon]: {
    width: { value: 48, unit: 'px' },
    height: { value: 48, unit: 'px' },
  },
  [ImageKey.GovernmentIdNfcScanBackClosed]: {
    width: { value: 250, unit: 'px' },
    height: { value: 250, unit: 'px' },
  },
  [ImageKey.GovernmentIdNfcScanFrontClosed]: {
    width: { value: 250, unit: 'px' },
    height: { value: 250, unit: 'px' },
  },
  [ImageKey.GovernmentIdNfcScanPhotoPage]: {
    width: { value: 250, unit: 'px' },
    height: { value: 250, unit: 'px' },
  },
  [ImageKey.GovernmentIdNfcScanIdCard]: {
    width: { value: 250, unit: 'px' },
    height: { value: 250, unit: 'px' },
  },
  [ImageKey.GovernmentIdNfcScanBackThenFront]: {
    width: { value: 250, unit: 'px' },
    height: { value: 250, unit: 'px' },
  },
  [ImageKey.StepperIncompleteIcon]: {
    width: { value: 20, unit: 'px' },
    height: { value: 20, unit: 'px' },
  },
  [ImageKey.StepperCurrentIcon]: {
    width: { value: 20, unit: 'px' },
    height: { value: 20, unit: 'px' },
  },
  [ImageKey.StepperErroredIcon]: {
    width: { value: 20, unit: 'px' },
    height: { value: 20, unit: 'px' },
  },
  [ImageKey.StepperCompletedIcon]: {
    width: { value: 20, unit: 'px' },
    height: { value: 20, unit: 'px' },
  },
  [ImageKey.StepperConnectorIcon]: {
    width: { value: 10, unit: 'px' },
    height: { value: 32, unit: 'px' },
  },
};

interface ImageLocalProps {
  component: ImageLocalComponent;
  inquiry?: Inquiry;
}

const ImageLocal = ({ component, inquiry }: ImageLocalProps) => {
  const {
    attributes: { 'image-key': imageKey, hidden },
  } = component;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const apply = useApply(component.name, inquiry?.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  if (isHidden) {
    return null;
  }

  const height = sizeCss(component.styles?.height?.base.base);
  const width = sizeCss(component.styles?.width?.base.base);
  const marginTop = sizeCss(component.styles?.margin?.base.base?.top);
  const marginBottom = sizeCss(component.styles?.margin?.base.base?.bottom);
  const marginLeft = sizeCss(component.styles?.margin?.base.base?.left);
  const marginRight = sizeCss(component.styles?.margin?.base.base?.right);
  const justify = component.styles?.justify.base.base;
  const colorPrimary = colorCss(component.styles?.['fill-color']?.base?.base);
  const colorBackground = colorCss(component.styles?.['fill-color']?.background.base);
  const colorHighlight = colorCss(component.styles?.['fill-color']?.highlight.base);
  const colorStroke = colorCss(component.styles?.['stroke-color']?.base?.base);

  const colorProps = {
    $primaryColor: colorPrimary,
    $backgroundColor: colorBackground,
    $highlightColor: colorHighlight,
    $strokeColor: colorStroke,
  };

  const fallbackDimensions = IMAGE_LOCAL_DIMENSION_DEFAULTS[imageKey];
  const centeredPictographMargins = { marginTop, marginBottom, marginLeft, marginRight };

  // color replacements for the nfc animations
  const nfcAnimationsColorReplacement = [
    ['#290087', colorStroke],
    ['#322B7E', colorStroke],
    ['#CBB2FF', colorPrimary],
    ['#F1E9FF', colorHighlight],
    ['#FFFFFF', colorBackground],
  ].filter(
    (value): value is [string, string] => value[1] != null && value[0] !== value[1].toUpperCase(),
  );

  switch (imageKey) {
    case ImageKey.AnimatedCheck:
      // NOTE: Unlike other image-keys, the animated check doesn't take theme icon colors.
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <AnimatedCheckContainer
          role='img'
          aria-label='Checkmark icon'
          data-test='image-local__animated-check'
          marginTop={marginTop}
          marginBottom={marginBottom}
          justify={justify}
          style={{ marginLeft, marginRight }}
        >
          <AnimatedCheck $primaryColor={colorPrimary} $backgroundColor={colorBackground} />
        </AnimatedCheckContainer>
      );
    case ImageKey.FailedIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Failed icon'
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg {...colorProps}>
            <ImageFailed
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );
    case ImageKey.StartHero:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Start hero image'
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <StartHero
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    // Default Failure Reason
    case ImageKey.FailureDefaultIcon:
      return (
        <FailureReasonPictograph role='img' aria-label='Failed icon' justify={justify}>
          <ThemedSvg {...colorProps}>
            <ImageDefaultFailed
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
                marginTop,
                marginBottom,
                marginLeft,
                marginRight,
              }}
            />
          </ThemedSvg>
        </FailureReasonPictograph>
      );

    // Document Failure Reasons
    case ImageKey.FailureDocumentIcon:
      return (
        <FailureReasonPictograph role='img' aria-label='Document icon' justify={justify}>
          <ThemedSvg {...colorProps}>
            <ImageDocumentFailed
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
                marginTop,
                marginBottom,
                marginLeft,
                marginRight,
              }}
            />
          </ThemedSvg>
        </FailureReasonPictograph>
      );

    // Selfie Failure Reason
    case ImageKey.FailureSelfieIcon:
      return (
        <FailureReasonPictograph role='img' aria-label='Selfie icon' justify={justify}>
          <ThemedSvg {...colorProps}>
            <ImageSelfieFail
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
                marginTop,
                marginBottom,
                marginLeft,
                marginRight,
              }}
            />
          </ThemedSvg>
        </FailureReasonPictograph>
      );

    // GovId Failure Reasons
    case ImageKey.IdBackIcon:
      return (
        <FailureReasonPictograph role='img' aria-label='ID back icon' justify={justify}>
          <ThemedSvg {...colorProps}>
            <ImageIdBackFail
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
                marginTop,
                marginBottom,
                marginLeft,
                marginRight,
              }}
            />
          </ThemedSvg>
        </FailureReasonPictograph>
      );
    case ImageKey.IdFrontIcon:
      return (
        <FailureReasonPictograph role='img' aria-label='ID front icon' justify={justify}>
          <ThemedSvg {...colorProps}>
            <ImageIdFrontFail
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
                marginTop,
                marginBottom,
                marginLeft,
                marginRight,
              }}
            />
          </ThemedSvg>
        </FailureReasonPictograph>
      );

    case ImageKey.DocumentStartHero:
      return (
        // a11y attributes are already defined on VerificationHero
        <CenteredPictograph
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          height={null}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          width={null}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          role={null}
          aria-label={null}
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps} style={{ width: '100%' }}>
            <VerificationHero backgroundColor={colorHighlight}>
              <ImageDocument height={80} width='100%' />
            </VerificationHero>
          </ThemedSvg>
        </CenteredPictograph>
      );
    case ImageKey.OptionFlagIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Flag option icon'
          aria-hidden={true} // inline icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageFlag
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionGlobeIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Globe option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageGlobe
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionHomeIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Home option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageHome
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionIdIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='ID option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageIdHorizontal
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionPhoneIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Phone option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImagePhone
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionIdDocumentIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Document ID option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageIdDocument
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionUserIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='User option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageUser
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionCheckIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Check option icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <ImageCheck
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.OptionChevronIcon:
      return (
        <CenteredPictograph
          role='img'
          aria-label='Chevron icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          justify={justify}
          {...centeredPictographMargins}
        >
          <ThemedSvg vectorEffect='initial' {...colorProps}>
            <IconChevron
              style={{
                width: width ?? sizeCss(fallbackDimensions.width),
                height: height ?? sizeCss(fallbackDimensions.height),
              }}
            />
          </ThemedSvg>
        </CenteredPictograph>
      );

    case ImageKey.CreatePersonaCtaCardIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Create Persona card icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <CreatePersonaCtaIcon
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );

    case ImageKey.CreatePersonaPasskeyRegistrationHero:
      return (
        <Lottie
          role='img'
          animationData={PasskeyRegistrationHero}
          aria-label='Create Persona passkey registration hero image'
          loop={true}
          style={{
            // Context: INC-427
            // Lottie animations that are larger than ~1100px can cause crashes
            // on WebView. There's no legitimate reason to render such a large
            // Lottie anyways, because on the mobile layout the max screen
            // width is 768px, and on the desktop layout the max widget width
            // is 386px.
            width: `min(${width ?? sizeCss(fallbackDimensions.width)}, 1000px)`,
            height: `min(${height ?? sizeCss(fallbackDimensions.height)}, 768px)`,
          }}
        />
      );

    case ImageKey.CreatePersonaPasskeyRegistrationSpeedIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Create Persona passkey registration icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <ImageSpeed
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );

    case ImageKey.CreatePersonaPasskeyRegistrationShieldIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Create Persona passkey registration icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <ImageShield
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );

    case ImageKey.CreatePersonaPasskeyRegistrationFingerprintIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Create Persona passkey registration icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <ImageFingerprint
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );

    case ImageKey.CreatePersonaSuccessHero:
      return (
        <Lottie
          role='img'
          aria-label='Create Persona success hero image'
          animationData={CreatePersonaSuccessHero}
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
            // Context: INC-427
            // Lottie animations that are larger than ~1100px can cause crashes
            // on WebView. There's no legitimate reason to render such a large
            // Lottie anyways, because on the mobile layout the max screen
            // width is 768px, and on the desktop layout the max widget width
            // is 386px.
            maxHeight: '1000px',
            maxWidth: '768px',
          }}
        />
      );

    case ImageKey.VerifyWithPersonaPasskeyAuthenticationHero:
      return (
        <Lottie
          role='img'
          animationData={PasskeyAuthenticationHero}
          aria-label='Verify with Persona passkey authentication hero image'
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );

    case ImageKey.VerifyWithPersonaPasskeyAuthenticationPersonaLogo:
      return (
        <ThemedSvg
          role='img'
          aria-label='Verify with Persona passkey authentication logo'
          vectorEffect='initial'
          {...colorProps}
        >
          <PersonaLogo
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationAddressIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Verify with Persona passkey authentication address icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <VerifyWithPersonaAddressIcon
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationIdIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Verify with Persona passkey authentication ID icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <VerifyWithPersonaIdIcon
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationIndividualAttributesIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Verify with Persona passkey authentication individual attributes icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <VerifyWithPersonaIndividualAttributesIcon
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationNameIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Verify with Persona passkey authentication name icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <VerifyWithPersonaNameIcon
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationSelfieIcon:
      return (
        <ThemedSvg
          role='img'
          aria-label='Verify with Persona passkey authentication selfie icon'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          vectorEffect='initial'
          {...colorProps}
        >
          <VerifyWithPersonaSelfieIcon
            style={{
              width: width ?? sizeCss(fallbackDimensions.width),
              height: height ?? sizeCss(fallbackDimensions.height),
            }}
          />
        </ThemedSvg>
      );
    case ImageKey.GovernmentIdNfcScanBackClosed:
      return (
        <Lottie
          role='img'
          aria-label='Government Id Nfc scan back of closed id image'
          animationData={replaceColors(
            nfcAnimationsColorReplacement,
            GovernmentIdNfcScanBackClosed,
          )}
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.GovernmentIdNfcScanFrontClosed:
      return (
        <Lottie
          role='img'
          aria-label='Government Id Nfc scan front of closed id image'
          animationData={replaceColors(
            nfcAnimationsColorReplacement,
            GovernmentIdNfcScanFrontClosed,
          )}
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.GovernmentIdNfcScanPhotoPage:
      return (
        <Lottie
          role='img'
          aria-label='Government Id Nfc scan photo page image'
          animationData={replaceColors(nfcAnimationsColorReplacement, GovernmentIdNfcScanPhotoPage)}
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.GovernmentIdNfcScanIdCard:
      return (
        <Lottie
          role='img'
          aria-label='Government Id Nfc scan id card image'
          animationData={replaceColors(nfcAnimationsColorReplacement, GovernmentIdNfcScanIdCard)}
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.GovernmentIdNfcScanBackThenFront:
      return (
        <Lottie
          role='img'
          aria-label='Government Id Nfc scan back then front of id image'
          animationData={replaceColors(
            nfcAnimationsColorReplacement,
            GovernmentIdNfcScanBackThenFront,
          )}
          loop={true}
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.StepperIncompleteIcon:
      return (
        <StepperIncompleteIcon
          role='img'
          aria-label='incomplete step'
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.StepperCurrentIcon:
      return (
        <StepperCurrentIcon
          role='img'
          aria-label='current step'
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.StepperCompletedIcon:
      return (
        <StepperCompletedIcon
          role='img'
          aria-label='step completed'
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.StepperErroredIcon:
      return (
        <StepperErroredIcon
          role='img'
          aria-label='step errored'
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    case ImageKey.StepperConnectorIcon:
      return (
        <StepperConnectorIcon
          role='img'
          aria-hidden={true} // icons shouldn't be announced by screen readers
          style={{
            width: width ?? sizeCss(fallbackDimensions.width),
            height: height ?? sizeCss(fallbackDimensions.height),
          }}
        />
      );
    default:
      return null;
  }
};

export default ImageLocal;
